/** @format */

import { AfterViewInit, Component, OnInit } from '@angular/core';
import { Chain, KillSwitches, Order } from '@shared/data-access/interfaces';
import { MyAccountAnalyticsService, Store, TrackOrdersService } from '@shared/data-access/services';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { finalize, first, tap } from 'rxjs/operators';

import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { ConstantsService } from '@my-account/data-access/services';
import { Title } from '@angular/platform-browser';
import { TrackOrdersAnalyticsTranslator } from './translators/track-orders-analytics.translator';
import { UtilityService } from '@shared/utils/services';
import { validateEuropeanDomain } from '@my-account/utils/validators';

@Component({
	selector: 'my-account-templates-order-track',
	templateUrl: './order-track.component.html',
	styleUrls: ['./order-track.component.scss']
})
export class OrderTrackComponent implements OnInit, AfterViewInit {
	showSpinner = false;
	initialLoad = true;
	postalCodeCountry: string = this.constants.countries.unitedStates.abbrev;

	resetCheckIn = new BehaviorSubject<boolean>(false);

	orderTrackForm: UntypedFormGroup;

	orderDetail: Order;

	errorMessage: string;

	initialTrackOrder = true;

	trackOrderByEmailEnabled = false;

	constructor(
		private chain: Chain,
		private store: Store,
		private utilityService: UtilityService,
		private formBuilder: UntypedFormBuilder,
		private service: TrackOrdersService,
		private route: ActivatedRoute,
		public constants: ConstantsService,
		private analyticsService: MyAccountAnalyticsService,
		private analyticsTranslator: TrackOrdersAnalyticsTranslator,
		private titleService: Title) { }

	ngOnInit() {
		this.buildOrderTrack();
		this.getKillswitchValues().subscribe(() => {
			this.route.queryParams.subscribe((params) => {
				const orderId = params['orderId'];
				let zip = params['zipCode'];
				const email = params['emailAddress'];

				if (!this.trackOrderByEmailEnabled && zip) {
					const dash = zip.indexOf('-');
					if (dash !== -1) {
						zip = zip.substr(0, dash);
					}
					const zipAsNumber = Number(zip);
					if (isNaN(zipAsNumber)) {
						this.showCanadianPostalCode();
						this.canadianPostalControl.setValue(zip);
					} else {
						this.showUSZipCode();
						this.zipPostalControl.setValue(zip);
					}
				} else if (this.trackOrderByEmailEnabled && email) {
					this.emailControl.setValue(email);
				}

				this.orderNumberControl.setValue(orderId);

				if (this.orderNumberControl.value && ((!this.trackOrderByEmailEnabled && this.zipPostalControl.value || this.canadianPostalControl.value) || (this.trackOrderByEmailEnabled && this.emailControl.value))) {
					this.trackOrder(null);
				}
			});
		});

		this.titleService.setTitle(this.route.snapshot.data.title);
	}

	ngAfterViewInit() {
		this.initialLoad = false;
	}

	buildOrderTrack() {
		this.orderTrackForm = this.formBuilder.group(
			{
				orderNumber: [],
				zip: [],
				email: ['', { validators: [Validators.email, validateEuropeanDomain] } ],
				canadianPostal: []
			}
		);
	}

	showCanadianPostalCode() {
		this.postalCodeCountry = this.constants.countries.canada.abbrev;
	}

	showUSZipCode() {
		this.postalCodeCountry = this.constants.countries.unitedStates.abbrev;
	}

	get orderNumberControl() {
		return this.orderTrackForm.controls['orderNumber'];
	}

	get zipPostalControl() {
		return this.orderTrackForm.controls['zip'];
	}

	get canadianPostalControl() {
		return this.orderTrackForm.controls['canadianPostal'];
	}

	get emailControl() {
		return this.orderTrackForm.controls['email'];
	}

	isSubmitDisabled() {
		if (!this.trackOrderByEmailEnabled) {
			const isCanadianZip = this.postalCodeCountry === this.constants.countries.canada.abbrev;

			if (isCanadianZip) {
				return !(this.orderNumberControl.value && this.canadianPostalControl.value);
			}

			return !(this.orderNumberControl.value && this.zipPostalControl.value);
		} else {
			return !(this.orderNumberControl.value && this.emailControl.value && this.emailControl.valid);
		}
	}

	trackOrder(event) {
		if (this.showSpinner || this.isSubmitDisabled()) {
			return;
		}

		this.errorMessage = null;
		this.showSpinner = true;

		const zipCode = this.postalCodeCountry === this.constants.countries.unitedStates.abbrev ? this.zipPostalControl.value : this.canadianPostalControl.value.toString().toUpperCase();

		this.analyticsTranslator.resetTrackOrderFields(this.analyticsService);

		const trackOrderObservable = this.trackOrderByEmailEnabled ? this.service.getOrderDetailByEmail(this.orderNumberControl.value, this.emailControl.value) : this.service.getOrderDetail(this.orderNumberControl.value, zipCode);
		trackOrderObservable.pipe(
				finalize(() => {
					this.showSpinner = false;
				})
			)
			.subscribe(
				(response) => {
					this.orderDetail = response;
					this.errorMessage = null;
					this.orderTrackForm.markAsPristine();
					this.analyticsTranslator.toViewModel(response, this.analyticsService);
					this.analyticsService.setEventType('TrackOrderClick');
					this.analyticsService.triggerAnalyticsEvent('TrackOrderClick');

					if (!this.initialTrackOrder) {
						this.resetCheckIn.next(true);
					}

					this.initialTrackOrder = false;
				},
				(error) => {
					this.orderDetail = null;
					this.errorMessage = error;

					this.setAnalyticsError(event);

					this.analyticsService.triggerAnalyticsEvent('SiteError');
				}
			);
	}

	private setAnalyticsError(event) {
		const errorMessage = `${event.target.id}:${this.errorMessage}`;
		this.analyticsTranslator.toTrackOrderError('inline', errorMessage, this.analyticsService);
	}

	private getKillswitchValues() {
		return this.store
			.select('ENVIRONMENT_KILLSWITCH_VALUES')
			.pipe(
				first(),
				tap((killswitch: KillSwitches) => {
					this.trackOrderByEmailEnabled = this.utilityService.checkKillSwitch('trackOrderByEmailEnabled', killswitch, this.chain.chainIdentifierAbbr);
				})
			);
	}
}
